import { type ReactNode } from "react"
import { Alert, Link, Stack, Typography } from "@octopus-energy/coral/atoms"
import IntlMessageFormat from "intl-messageformat"
import { useLocale, useTranslations } from "next-intl"
import { useFeatureFlag } from "@/contexts/FeatureFlagsProvider"
import { type SelectedEnergyTypes } from "@/features/subscription/types/selectedEnergyTypes"
import { type OnboardingMaintenanceAlertConfig } from "@/lib/flags/types/onboardingMaintenanceAlert"
import { externalRoutesPaths } from "@/routes/external"
import { type SalesChannelWithTelesales } from "@/scalars/salesChannelChoices"

// Generic onboarding error Typeform. Default target for the
// `<typeform>` tag when the flag doesn't provide a `typeformUrl`.
const ONBOARDING_ERROR_TYPEFORM_URL = externalRoutesPaths.octopusTypeform.resolve({
    pathParams: { formId: "LeoKQ2s3" },
})

/**
 * Renders a flag-provided string using the same next-intl rich text tags as the
 * message catalog (`<b>`, `<br>`, `<typeform>`), so authoring is consistent across
 * the app. Powered by `intl-messageformat`, which is the engine behind next-intl.
 */
function useRenderRichText(config: OnboardingMaintenanceAlertConfig) {
    const locale = useLocale()

    return (message: string): ReactNode => {
        return new IntlMessageFormat(message, locale).format<ReactNode>({
            br: () => <br />,
            b: (chunks) => <b>{chunks}</b>,
            typeform: (chunks) => (
                <Link
                    href={config.wordings?.typeformUrl ?? ONBOARDING_ERROR_TYPEFORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {chunks}
                </Link>
            ),
        }) as ReactNode
    }
}

function isWithinMaintenanceWindow(now: Date, window: { start: string; end: string }) {
    const start = new Date(window.start)
    const end = new Date(window.end)

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return false
    }

    return now >= start && now <= end
}

function shouldDisplayAlert(
    config: OnboardingMaintenanceAlertConfig,
    props: OnboardingMaintenanceAlertProps
) {
    if (!config.enabled) {
        return false
    }

    // When a maintenance window is provided, the alert is gated by it.
    // Otherwise `enabled` alone controls the display (on/off mode).
    if (
        config.maintenanceWindow &&
        !isWithinMaintenanceWindow(new Date(), config.maintenanceWindow)
    ) {
        return false
    }

    // When targeting energies, at least one selected energy must match.
    if (
        config.energyTypes &&
        !config.energyTypes.some((energy) => props.energyTypes.includes(energy))
    ) {
        return false
    }

    // When targeting sales channels, the current channel must be listed.
    if (config.salesChannels && !config.salesChannels.includes(props.salesChannel)) {
        return false
    }

    return true
}

type OnboardingMaintenanceAlertProps = {
    /** Energies of the current subscription, used to match `config.energyTypes`. */
    energyTypes: SelectedEnergyTypes
    /** Sales channel of the current subscription, used to match `config.salesChannels`. */
    salesChannel: SalesChannelWithTelesales
}

export function OnboardingMaintenanceAlert({
    energyTypes,
    salesChannel,
}: OnboardingMaintenanceAlertProps) {
    const t = useTranslations("OnboardingMaintenanceAlert")
    const config = useFeatureFlag("onboardingMaintenanceAlert")
    const renderRichText = useRenderRichText(config)

    if (!shouldDisplayAlert(config, { energyTypes, salesChannel })) {
        return null
    }

    const title = config.wordings?.title ?? t("title")
    const description = config.wordings?.description ?? t("description")

    return (
        <Alert
            icon={false}
            severity="info"
        >
            <Stack flexDirection="column">
                <Typography textStyle="h5">{title}</Typography>
                <Typography>{renderRichText(description)}</Typography>
            </Stack>
        </Alert>
    )
}

OnboardingMaintenanceAlert.messages = ["OnboardingMaintenanceAlert"] as const
